import React from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const ProductionLossTracker = ({ lossData, onChange, errors }) => {
  const lossTypeOptions = [
    { value: 'spillage', label: 'Tumpahan/Spillage' },
    { value: 'contamination', label: 'Kontaminasi' },
    { value: 'equipment_failure', label: 'Kerusakan Alat' },
    { value: 'quality_issue', label: 'Masalah Kualitas' },
    { value: 'expired', label: 'Kadaluarsa' },
    { value: 'other', label: 'Lainnya' }
  ];

  const handleInputChange = (field, value) => {
    onChange({ ...lossData, [field]: value });
  };

  const calculateTotalLoss = () => {
    const fields = ['spillageLoss', 'contaminationLoss', 'equipmentLoss', 'qualityLoss', 'otherLoss'];
    return fields?.reduce((sum, field) => sum + (parseFloat(lossData?.[field]) || 0), 0);
  };

  const calculateLossPercentage = () => {
    const total = parseFloat(lossData?.totalProduction) || 0;
    const loss = calculateTotalLoss();
    if (total === 0) return 0;
    return ((loss / total) * 100)?.toFixed(2);
  };

  return (
    <div className="card space-y-6">
      <div className="flex items-center gap-3 pb-4 border-b border-border">
        <div className="w-12 h-12 rounded-xl bg-warning/10 flex items-center justify-center">
          <Icon name="TrendingDown" size={24} color="var(--color-warning)" />
        </div>
        <div>
          <h2 className="text-xl font-semibold">Tracking Kehilangan Produksi</h2>
          <p className="text-sm text-muted-foreground">Identifikasi dan kuantifikasi production loss</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        <div className="md:col-span-2">
          <Input
            label="Total Produksi (Liter/Bulan)"
            type="number"
            placeholder="Contoh: 150000"
            value={lossData?.totalProduction || ''}
            onChange={(e) => handleInputChange('totalProduction', e?.target?.value)}
            error={errors?.totalProduction}
            required
            description="Volume produksi total sebagai baseline"
          />
        </div>

        <Input
          label="Kehilangan - Tumpahan (Liter)"
          type="number"
          placeholder="Contoh: 500"
          value={lossData?.spillageLoss || ''}
          onChange={(e) => handleInputChange('spillageLoss', e?.target?.value)}
          description="Loss akibat tumpahan dalam proses"
        />

        <Input
          label="Kehilangan - Kontaminasi (Liter)"
          type="number"
          placeholder="Contoh: 800"
          value={lossData?.contaminationLoss || ''}
          onChange={(e) => handleInputChange('contaminationLoss', e?.target?.value)}
          description="Loss akibat kontaminasi produk"
        />

        <Input
          label="Kehilangan - Kerusakan Alat (Liter)"
          type="number"
          placeholder="Contoh: 1200"
          value={lossData?.equipmentLoss || ''}
          onChange={(e) => handleInputChange('equipmentLoss', e?.target?.value)}
          description="Loss akibat equipment failure"
        />

        <Input
          label="Kehilangan - Kualitas (Liter)"
          type="number"
          placeholder="Contoh: 600"
          value={lossData?.qualityLoss || ''}
          onChange={(e) => handleInputChange('qualityLoss', e?.target?.value)}
          description="Loss akibat tidak memenuhi standar"
        />

        <Input
          label="Kehilangan - Lainnya (Liter)"
          type="number"
          placeholder="Contoh: 300"
          value={lossData?.otherLoss || ''}
          onChange={(e) => handleInputChange('otherLoss', e?.target?.value)}
          description="Loss dari faktor lain"
        />

        <Select
          label="Kategori Loss Terbesar"
          options={lossTypeOptions}
          value={lossData?.primaryLossType || ''}
          onChange={(value) => handleInputChange('primaryLossType', value)}
          description="Jenis kehilangan yang paling dominan"
        />
      </div>
      {lossData?.totalProduction && (
        <div className="mt-6 space-y-4">
          <div className="p-4 rounded-lg bg-muted">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Total Kehilangan</p>
                <p className="text-2xl font-bold text-warning data-text">{calculateTotalLoss()?.toFixed(0)} L</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Persentase Loss</p>
                <p className="text-2xl font-bold text-error data-text">{calculateLossPercentage()}%</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Status</p>
                <p className={`text-lg font-bold ${parseFloat(calculateLossPercentage()) > 3 ? 'text-error' : 'text-success'}`}>
                  {parseFloat(calculateLossPercentage()) > 3 ? 'Tinggi' : 'Normal'}
                </p>
              </div>
            </div>
          </div>

          {parseFloat(calculateLossPercentage()) > 3 && (
            <div className="p-4 rounded-lg bg-error/10 border border-error/20 flex items-start gap-3">
              <Icon name="AlertTriangle" size={20} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-error">Peringatan: Tingkat Kehilangan Tinggi</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Production loss melebihi 3% dari total produksi. Diperlukan analisis mendalam dan action plan untuk mengurangi kehilangan.
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductionLossTracker;